import { KycStatus, QesStatus } from '__generated__/GQL';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';

import getComplianceAndPersonalAddressStatus from '../../ComplianceAndPersonalAddress/libs/getComplianceAndPersonalAddressStatus';
import getVerificationSteps from '../lib/steps/getVerificationSteps';
import { VerificationStatus } from '../lib/steps/types';
import { State } from './machine';
import { Model } from './model';

export enum Guard {
  ShouldSkipHomeToIdentity = 'shouldSkipHomeToIdentity',
  ShouldSkipHomeToSubmit = 'shouldSkipHomeToSubmit',
  IsComplianceAndPersonalAddressDone = 'isComplianceAndPersonalAddressDone',
  IsMandatee = 'isMandatee',
  IsQesTodo = 'isQesTodo',
  IsIdentityReviewInvalid = 'isIdentityReviewInvalid',
}

const guards: MachineGuards<Model, Guard> = {
  isComplianceAndPersonalAddressDone: (context) => {
    const { applicantType, verificationData } = context.getModuleData();
    const { profile } = verificationData.viewer;

    return (
      getComplianceAndPersonalAddressStatus({
        applicantType,
        userProfile: profile,
      }) === VerificationStatus.Completed
    );
  },

  isIdentityReviewInvalid: (context) => {
    const { companyUser } = context.getModuleData().verificationData.viewer;

    return companyUser.kycIdStatus === KycStatus.Refused;
  },

  isMandatee: (context) =>
    context.getModuleData().applicantType === ApplicantType.Mandatee,

  isQesTodo: ({ getModuleData }) => {
    const { qesStatus } = getModuleData().verificationData.viewer.companyUser;

    return qesStatus === QesStatus.Todo;
  },

  /**
   * We only skip the Verification Home when KycReview.error is null,
   * there's a single step, the step is Identity, and its status is Todo.
   */
  shouldSkipHomeToIdentity: ({ getModuleData }) => {
    const moduleData = getModuleData();
    const { applicantType, applicationType, verificationData } = moduleData;
    const { isMicro } = verificationData.viewer.company.profile;

    const isOnboardingHub =
      applicantType === ApplicantType.AccountHolder &&
      applicationType === ApplicationType.Standard;

    if (isOnboardingHub && !isMicro) {
      return false;
    }

    const { lastKycReview } = verificationData.viewer.companyUser;

    if (lastKycReview?.error) {
      return false;
    }

    const steps = getVerificationSteps(moduleData);

    /**
     * Don't count statutes, they may have been auto-fetched
     */
    const stepsFiltered = steps.filter(({ id, status }) => {
      /**
       * These two modules are probably not made by the user.
       * We therefore don't count them.
       */
      const isPotentiallyAutoFetched =
        (id === State.STATUTES || id === State.COMPANY_PAPERS) &&
        (status === VerificationStatus.Completed ||
          status === VerificationStatus.Validated);

      return !isPotentiallyAutoFetched;
    });

    return (
      stepsFiltered.length === 1 &&
      // @ts-expect-error Object is possibly 'undefined'.
      stepsFiltered[0].id === State.IDENTITY &&
      [VerificationStatus.ToDo, VerificationStatus.Invalid].includes(
        // @ts-expect-error Object is possibly 'undefined'.
        stepsFiltered[0].status,
      )
    );
  },

  shouldSkipHomeToSubmit: ({ getModuleData }) => {
    const moduleData = getModuleData();
    const { applicantType, applicationType, verificationData } = moduleData;
    const { isMicro } = verificationData.viewer.company.profile;

    const isOnboardingHub =
      applicantType === ApplicantType.AccountHolder &&
      applicationType === ApplicationType.Standard;

    if (isOnboardingHub && !isMicro) {
      return false;
    }

    const steps = getVerificationSteps(moduleData);

    const numberOfRemainingSteps = steps.filter(
      ({ status }) =>
        status !== VerificationStatus.Completed &&
        status !== VerificationStatus.Validated,
    ).length;

    return numberOfRemainingSteps === 0;
  },
};

export default guards;
